// Phase 1A Step 34 — Section 07 Pricing.
// Verbatim port from handoff/landing-reference/sections.jsx PricingSection.
// Currency toggle (GHS/NGN) + view toggle (tiers/bundles).

import { BUNDLES, type Currency, TIERS } from "./landing-data.ts";
import { BundleCard } from "./BundleCard.tsx";
import { PriceCard } from "./PriceCard.tsx";

interface PricingProps {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  pricingMode: "tiers" | "bundles";
  setPricingMode: (m: "tiers" | "bundles") => void;
}

export function Pricing({
  currency,
  setCurrency,
  pricingMode,
  setPricingMode,
}: PricingProps): JSX.Element {
  const isBundles = pricingMode === "bundles";
  return (
    <section
      id="pricing"
      className="sz-l-section"
      style={{ padding: "160px 0 180px", borderTop: "1px solid var(--border)" }}
    >
      <div className="container">
        <div
          className="sz-l-pricing-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 48,
            flexWrap: "wrap",
            gap: 32,
          }}
        >
          <div>
            <span className="bracket">Section 07 · Pricing</span>
            <h2
              className="serif"
              style={{
                marginTop: 24,
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                color: "var(--text)",
                letterSpacing: "-0.01em",
                lineHeight: 1.02,
              }}
            >
              {isBundles ? "Pick your markets." : "Six tiers."}
              <br />
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                {isBundles ? "Add to any subscription." : "From entry to wealth advisory."}
              </em>
            </h2>
            {/*
              GAP-09 trust badge — surface the Verified Return Certificate
              on the Pricing header per STRIKZA_OddsJam_Gaps_v1.0.docx
              ("trust badge displayed on the platform homepage and pricing
              page"). Doctrinal posture: we sell the verification mechanism,
              not a win-rate.
            */}
            <a
              href="#ledger"
              style={{
                marginTop: 22,
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                border: "1px solid var(--accent-deep)",
                background: "rgba(212, 168, 67, 0.06)",
                color: "var(--text)",
                textDecoration: "none",
                fontSize: 12,
                lineHeight: 1.3,
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.18em",
                  color: "var(--accent)",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                Verified Return · in audit
              </span>
              <span style={{ color: "var(--text-muted)" }}>
                Third-party 90-day CLV+ record published within 4 months of launch — across all
                tiers
              </span>
              <span
                className="mono"
                style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.1em" }}
              >
                →
              </span>
            </a>
          </div>

          {/* GHS / NGN toggle */}
          <div
            style={{
              display: "inline-flex",
              border: "1px solid var(--border-bright)",
              padding: 4,
              background: "var(--surface)",
            }}
          >
            {[
              { id: "GHS" as const, lbl: "GH₵ Ghana" },
              { id: "NGN" as const, lbl: "₦ Nigeria" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setCurrency(o.id)}
                className="mono"
                style={{
                  padding: "10px 18px",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  background: currency === o.id ? "var(--accent)" : "transparent",
                  color: currency === o.id ? "#0A0A0A" : "var(--text-muted)",
                  border: 0,
                  cursor: "pointer",
                  transition: "all .15s",
                }}
              >
                {o.lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing-mode toggle */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 48,
            gap: 0,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              border: "1px solid var(--border-bright)",
              padding: 4,
              background: "var(--surface)",
            }}
          >
            {[
              { id: "tiers" as const, lbl: "Subscription tiers" },
              { id: "bundles" as const, lbl: "Expansion modules" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setPricingMode(o.id)}
                className="mono"
                style={{
                  padding: "10px 22px",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  background: pricingMode === o.id ? "var(--accent)" : "transparent",
                  color: pricingMode === o.id ? "#0A0A0A" : "var(--text-muted)",
                  border: 0,
                  cursor: "pointer",
                  transition: "all .15s",
                }}
              >
                {o.lbl}
              </button>
            ))}
          </div>
        </div>

        {isBundles ? (
          <div
            className="sz-l-pricing-bundles"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 16,
              alignItems: "start",
            }}
          >
            {BUNDLES.map((b) => (
              <BundleCard key={b.id} b={b} currency={currency} />
            ))}
          </div>
        ) : (
          <div
            className="sz-l-pricing-tiers"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
              alignItems: "start",
            }}
          >
            {TIERS.map((t) => (
              <PriceCard key={t.id} tier={t} featured={t.id === "pro"} currency={currency} />
            ))}
          </div>
        )}

        <p
          className="mono"
          style={{
            marginTop: 40,
            textAlign: "center",
            fontSize: 11,
            letterSpacing: "0.16em",
            color: "var(--text-dim)",
            textTransform: "uppercase",
          }}
        >
          {isBundles
            ? "Add modules to any tier · Crypto-paid modules available · Lifetime options for Polymarket / Forex / Crypto"
            : "7-day free trial · Cancel anytime · MTN MoMo · Telecel Cash · Paystack · Crypto from SIKA Pro"}
        </p>
      </div>
    </section>
  );
}
